import { isBrowser } from "./helpers";

const storageKey = "BS_RandomSeed";
// Largest value that fits in a GraphQL Int
const maxSeed = 2147483647;
// A new seed is created when the visitor has not used the site for this long
const seedLifetime = 24 * 60 * 60 * 1000;

let seed: number | null = null;

/**
 * Gets the seed for random sorting of lodgings.
 * It is created in the browser and shared by all tabs, so a visitor keeps the
 * same order while every visitor gets their own order.
 * It does not depend on cookies, so it also works for widgets on other domains.
 */
export function getRandomSeed(): number | null {
    if (!isBrowser()) {
        return null;
    }

    // Read once per page view, so the order does not change while the page is open
    if (seed === null) {
        seed = readStoredSeed() ?? createSeed();
        try {
            window.localStorage.setItem(storageKey, JSON.stringify({ seed, lastUsed: Date.now() }));
        } catch {
            // Storage can be blocked, then the seed only lasts for this page view
        }
    }
    return seed;
}

function readStoredSeed(): number | null {
    try {
        const stored = JSON.parse(window.localStorage.getItem(storageKey));
        const isValid = Number.isInteger(stored?.seed) && stored.seed >= 1 && stored.seed <= maxSeed;
        const isExpired = !(Date.now() - stored?.lastUsed < seedLifetime);
        return isValid && !isExpired ? stored.seed : null;
    } catch {
        return null;
    }
}

function createSeed(): number {
    if (window.crypto?.getRandomValues) {
        const values = new Uint32Array(1);
        window.crypto.getRandomValues(values);
        return (values[0] % maxSeed) + 1;
    }
    return Math.floor(Math.random() * maxSeed) + 1;
}
